import { Layout, Tabs, TabsProps, notification } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import QuickExceptionProcessing from './QuickExceptionProcessing'
import { getExceptionProcessing } from 'src/app/shared/services/TransactionService'
import { useDefaultSearchData } from 'src/app/shared/components/common/table/table.constants'
import { useClient } from 'src/app/shared/hooks/useClient'
import './index.scss'

export enum QuickExceptionSetTabs {
  Ach = 0,
  Check = 1,
  Total = 2,
}

const QuickExceptionSetup: React.FC = () => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const [api, contextHolder] = notification.useNotification()
  const [achDecisionsNeeded, setAchDecisionsNeeded] = useState<number>(0)
  const [checkDecisionsNeeded, setCheckDecisionsNeeded] = useState<number>(0)
  const [selectedClients, setSelectedClients] = useState<any>([null])
  const [selectedAccounts, setSelectedAccounts] = useState<any>([null])
  const [availableServices, setAvailableServices] = useState<{ hasAch: boolean; hasCheck: boolean } | null>(null)
  const [activeTab, setActiveTab] = useState<string>(QuickExceptionSetTabs.Ach.toString())
  const defaultSearchData = useDefaultSearchData()
  const { clients, loadingClients, handleGetClients, handleGetClientInfoByUser } = useClient()

  const handleAvailableServicesChange = useCallback((services: { hasAch: boolean; hasCheck: boolean }) => {
    setAvailableServices(services)
  }, [])

  // When at least one service is enabled for the selected clients, disable the tab of any
  // service none of them offers. If neither is enabled (indeterminate), keep both enabled.
  const hasAnyService = !!availableServices && (availableServices.hasAch || availableServices.hasCheck)
  const achDisabled = hasAnyService && !availableServices?.hasAch
  const checkDisabled = hasAnyService && !availableServices?.hasCheck

  useEffect(() => {
    if (!hasAnyService) return
    if (achDisabled && activeTab === QuickExceptionSetTabs.Ach.toString()) {
      setActiveTab(QuickExceptionSetTabs.Check.toString())
    } else if (checkDisabled && activeTab === QuickExceptionSetTabs.Check.toString()) {
      setActiveTab(QuickExceptionSetTabs.Ach.toString())
    }
  }, [hasAnyService, achDisabled, checkDisabled, activeTab])

  const fetchDecisionsNeeded = useCallback(async (type: QuickExceptionSetTabs) => {
    try {
      if (selectedClients?.length && selectedAccounts?.length) {
        const response = await getExceptionProcessing({
          ...defaultSearchData,
          clientIds: selectedClients?.includes(null) ? null : selectedClients,
          accountIds: selectedAccounts?.includes(null) ? null : selectedAccounts,
          exceptionSummaryType: 0, // Decisions needed
          types: [type],
          applyBlankIssuedFields: true
        })
        
        const decisionsNeeded = response?.data?.items?.decisionsNeededTotal || 0
        
        if (type === QuickExceptionSetTabs.Ach) {
          setAchDecisionsNeeded(decisionsNeeded)
        } else if (type === QuickExceptionSetTabs.Check) {
          setCheckDecisionsNeeded(decisionsNeeded)
        }
      }
    } catch (error) {
      console.error('Error fetching decisions needed:', error)
    }
  }, [selectedClients, selectedAccounts, defaultSearchData])

  useEffect(() => {
    if (selectedClients?.length && selectedAccounts?.length) {
      fetchDecisionsNeeded(QuickExceptionSetTabs.Ach)
      fetchDecisionsNeeded(QuickExceptionSetTabs.Check)
    }
  }, [selectedClients, selectedAccounts, fetchDecisionsNeeded])
  
  const tabs: TabsProps['items'] = [
    {
      key: QuickExceptionSetTabs.Ach.toString(),
      disabled: achDisabled,
      label: (
        <>
          <span className={achDecisionsNeeded === 0 ? 'success' : 'danger'}>{t('QuickExceptionSetup:quick_exception_setup.ach') + ' '}</span>
          <span className={`badge ${achDecisionsNeeded === 0 ? 'success' : 'danger'}`}>{t('QuickExceptionSetup:quick_exception_setup.ach_remaining', { ach: achDecisionsNeeded })}</span>
        </>
      ),
      children: <QuickExceptionProcessing
        type={QuickExceptionSetTabs.Ach}
        onDecisionsNeededChange={setAchDecisionsNeeded}
        onAvailableServicesChange={handleAvailableServicesChange}
        selectedClients={selectedClients}
        selectedAccounts={selectedAccounts}
        setSelectedClients={setSelectedClients}
        setSelectedAccounts={setSelectedAccounts}
        clients={clients}
        loadingClients={loadingClients}
        handleGetClients={handleGetClients}
        handleGetClientInfoByUser={handleGetClientInfoByUser}
      />,
    },
    {
      key: QuickExceptionSetTabs.Check.toString(),
      disabled: checkDisabled,
      label: (
        <>
          <span className={`${checkDecisionsNeeded === 0 ? 'success' : 'danger'}`}>{t('QuickExceptionSetup:quick_exception_setup.check') + ' '}</span>
          <span className={`badge ${checkDecisionsNeeded === 0 ? 'success' : 'danger'}`}>{t('QuickExceptionSetup:quick_exception_setup.check_remaining', { check: checkDecisionsNeeded })}</span>
        </>
      ),
      children: <QuickExceptionProcessing
        type={QuickExceptionSetTabs.Check}
        onDecisionsNeededChange={setCheckDecisionsNeeded}
        onAvailableServicesChange={handleAvailableServicesChange}
        selectedClients={selectedClients}
        selectedAccounts={selectedAccounts}
        setSelectedClients={setSelectedClients}
        setSelectedAccounts={setSelectedAccounts}
        clients={clients}
        loadingClients={loadingClients}
        handleGetClients={handleGetClients}
        handleGetClientInfoByUser={handleGetClientInfoByUser}
      />,
    },
    {
      key: QuickExceptionSetTabs.Total.toString(),
      label: achDecisionsNeeded === 0 && checkDecisionsNeeded === 0 
        ? t('QuickExceptionSetup:quick_exception_setup.all_exceptions_resolved') 
        : t('QuickExceptionSetup:quick_exception_setup.total_exceptions', { 
            total: achDecisionsNeeded + checkDecisionsNeeded
          }),
      disabled: true,
      children: null,
    },
  ]

  useEffect(() => {
    if (state && state?.length > 0) {
      state.forEach((item: any) => {
        item.displayToast &&
          api.success({
            message: <RichTextField richText={item?.message} />,
          })
      })
    }
  }, [state, api])

  useEffect(() => {
    // clear location state
    window.history.replaceState({}, '')
  }, [])

  return (
    <>
      {contextHolder}
      <Layout.Content
        style={{
          padding: 24,
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} destroyInactiveTabPane />
      </Layout.Content>
    </>
  )
}

export default QuickExceptionSetup
